import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { ResidenceCountryService } from '@lx/shared/data-access/destination';
import { getLocaleCountryCode } from '@lx/shared/i18n';

/**
 * Service to determine the appropriate currency for a given country of residence.
 *
 * This service applies business rules to determine whether to use:
 * - The market's currency (based on locale)
 * - The residence country's currency
 *
 * Rules are configurable per market via configuration parameters.
 */
@Injectable({
  providedIn: 'root',
})
export class ResidenceCurrencyService {
  private readonly marketCountry = getLocaleCountryCode();
  private readonly residenceCountryService = inject(ResidenceCountryService);

  /**
   * Gets the currency to use for a given country of residence.
   *
   * @param countryOfResidence - The country code of the user's residence
   * @param useMarketCurrency - If true, use market currency unless country is in useResidenceCurrencyCountries
   * @param useResidenceCurrencyCountries - List of country codes that should use their own currency regardless of useMarketCurrency
   * @returns Observable of the currency code (e.g., 'USD', 'EUR', 'GBP')
   *
   * @example
   * // Use market currency unless country is in allowed list
   * getResidenceCurrency('US', true, ['CA', 'MX']).subscribe(currency => {
   *   console.log(currency); // Returns 'USD' (residence currency)
   * });
   *
   * @example
   * // Always use residence currency
   * getResidenceCurrency('FR', false, []).subscribe(currency => {
   *   console.log(currency); // Returns 'EUR'
   * });
   */
  getResidenceCurrency(countryOfResidence: string, useMarketCurrency: boolean, useResidenceCurrencyCountries: string[]): Observable<string> {
    const country = this.getCountryToUseForCurrencySelection(countryOfResidence, useMarketCurrency, useResidenceCurrencyCountries);

    return this.residenceCountryService.getByCode(country).pipe(
      first(),
      map((residenceCountry) => residenceCountry?.currency ?? 'USD'),
    );
  }

  /**
   * Determines which country's currency to use based on business rules.
   *
   * @param countryOfResidence - The country code of the user's residence
   * @param useMarketCurrency - If true, prefer market currency
   * @param useResidenceCurrencyCountries - Countries that should always use their own currency
   * @returns Country code whose currency should be used
   * @private
   */
  private getCountryToUseForCurrencySelection(countryOfResidence: string, useMarketCurrency: boolean, useResidenceCurrencyCountries: string[]): string {
    if (!countryOfResidence) {
      // No country of residence -> default to market country
      return this.marketCountry;
    }

    // useResidenceCurrencyCountries takes precedence over useMarketCurrency
    // If country is NOT in the allowed list AND useMarketCurrency is enabled -> use market currency
    if (!useResidenceCurrencyCountries.includes(countryOfResidence) && useMarketCurrency) {
      return this.marketCountry;
    }

    // Use the residence country's currency
    return countryOfResidence;
  }
}
